/**
 * One page of results, with its unit cards folded underneath.
 *
 * The default grouping is page-first because a newspaper page is the thing
 * that was printed: four matches on one page are one piece of evidence, not
 * four, and a flat unit list makes a reader who has already read that page
 * read its header four times over. The thumbnail carries the page's own hit
 * regions drawn in the accent, so before expanding anything a reader can see
 * where on the sheet the matches fell and whether they cluster.
 *
 * The first three rows open by default, and every row opens when the result
 * set is small, so the common case needs no clicking to read.
 */

import { Link } from 'react-router-dom'
import type { SearchUnitResult } from '../../lib/api'
import { Chevron, CropPlaceholder } from '../Page'
import { ResultCard } from './ResultCard'
import { n, type PageGroup } from './model'

export function PageRow({
  group,
  query,
  open,
  onToggle,
  onClip,
  clipped,
}: {
  group: PageGroup
  query: string
  open: boolean
  onToggle: () => void
  onClip?: (unit: SearchUnitResult) => void
  clipped?: Set<string>
}) {
  const best = group.units[0]
  const label = group.section_label ?? (group.page_number ? `page ${group.page_number}` : null)
  const scale =
    group.width_px && group.height_px ? { width: group.width_px, height: group.height_px } : null

  return (
    <section className="border-b border-n300">
      <div className="flex items-start gap-3 px-4 py-4 sm:gap-[15px] sm:px-[22px]">
        <div className="w-[104px] flex-none">
          {/*
            No thumbnail is a different fact from no licence, and the
            placeholder says which: a page whose raster was never made says so,
            rather than showing a broken image or borrowing the sign-in wording
            from the licence denial.
          */}
          {group.restricted || !group.page_id || !group.thumbnail_url ? (
            <CropPlaceholder
              className="h-[140px] w-[104px]"
              lines={
                group.restricted || !group.page_id
                  ? ['PAGE', 'SIGN IN']
                  : ['NO PAGE', 'THUMBNAIL']
              }
            />
          ) : (
            <Link
              to={`/page/${group.page_id}?${new URLSearchParams({ q: query }).toString()}`}
              className="relative block overflow-hidden rounded-[1px] bg-well"
              aria-label={`Open ${[group.pub_date, label].filter(Boolean).join(' ')}`}
            >
              <img
                src={group.thumbnail_url}
                alt=""
                loading="lazy"
                className="block w-full"
              />
              {/*
                The hit regions, in the accent, in the page's own coordinate
                space. They are drawn only when the page's raster size is
                known: a box positioned against a guessed page size would point
                at the wrong column, which is worse than no box at all.
              */}
              {scale &&
                group.hit_regions.map((hit, index) => (
                  <span
                    key={`${hit.unit_id}-${index}`}
                    className="absolute"
                    style={{
                      left: `${(hit.region[0] / scale.width) * 100}%`,
                      top: `${(hit.region[1] / scale.height) * 100}%`,
                      width: `${((hit.region[2] - hit.region[0]) / scale.width) * 100}%`,
                      height: `${((hit.region[3] - hit.region[1]) / scale.height) * 100}%`,
                      border: '1.5px solid var(--jl-accent)',
                      background: 'rgba(122,34,51,0.16)',
                    }}
                    aria-hidden="true"
                  />
                ))}
            </Link>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10.5px] text-ink">
              {[group.pub_date, label].filter(Boolean).join(' · ')}
            </span>
            {group.publication_name && (
              <span className="font-mono text-[10px] uppercase text-ink2">
                {group.publication_name}
              </span>
            )}
            <span className="ml-auto flex items-center gap-3">
              {group.route && (
                <span className="font-mono text-[10px] text-ink2">route {group.route}</span>
              )}
              <span className="font-mono text-[11px] text-ink">
                {n(group.hit_count)} {group.hit_count === 1 ? 'hit' : 'hits'}
              </span>
            </span>
          </div>

          {/*
            The best unit's headline is a label for the row, not the result
            itself, so it is clamped: a mangled recognition of a whole column
            set as a headline would push every other row off the screen. The
            unclamped text is right below, in that unit's own card.
          */}
          {best && (
            <h3 className="m-0 mb-[5px] line-clamp-2 text-[20px] font-semibold leading-[1.2] tracking-[-0.008em]">
              <Link to={`/unit/${best.id}`} className="text-body no-underline hover:text-accent">
                {best.headline || <span className="text-ink2">Untitled {best.unit_type}</span>}
              </Link>
            </h3>
          )}

          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="flex items-center gap-1.5 bg-transparent p-0 font-mono text-[10px] tracking-[0.06em] text-accent"
          >
            <span className="inline-flex" style={{ transform: open ? 'rotate(90deg)' : 'none' }}>
              <Chevron direction="right" size={11} />
            </span>
            {open ? 'HIDE' : 'SHOW'} {n(group.units.length)}{' '}
            {group.units.length === 1 ? 'RESULT' : 'RESULTS'} ON THIS PAGE
          </button>
        </div>
      </div>

      {/* The accent rule ties the unit cards to the sheet they were printed on. */}
      {open && (
        <div
          className="border-t border-n200 sm:pl-[22px]"
          style={{ boxShadow: 'inset 2px 0 0 var(--jl-accent-200)' }}
        >
          {group.units.map((unit) => (
            <ResultCard
              key={unit.id}
              unit={unit}
              widthPx={group.width_px}
              heightPx={group.height_px}
              query={query}
              onClip={onClip}
              clipped={clipped?.has(unit.id)}
              dense
            />
          ))}
        </div>
      )}
    </section>
  )
}
